import React, { useCallback, useState, useEffect, useRef } from 'react';
import EntityTable, { ENTITY_TABLE_PAGE_SIZE } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { GeneratingModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/GeneratingModal';
import color from 'src/utils/colors';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import NestedPill from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/components/NestedPill';
import { useProductTable } from './useProductTable';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SelectProfileModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/SelectProfileModal';
import { SingleSelectProfileModal } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/SingleSelectProfileModal';
import { useProfileAssignment } from './useProfileAssignment';
import { GenerateContentForSkusRequest } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/api/apiTypes';
import { useGenerateContentForSkus } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/hooks/useContentGenerate';
import { getQueryParamValue } from 'src/utils/app';
import { useContentGenerationStore } from './store';
import { useSnackbar } from 'src/utils/Hooks';
import { useBeaconClientId } from 'src/utils/Hooks/reduxSelectors';
import { SnackbarCheckmark, BlueSearch, BluePencil } from 'src/components/SvgIcons/SvgIcons';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { useContentGenReviewStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/stores';
import { GenerationStatusEnum } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Generate/types';
import {
  useCreateProfile,
  useGetProfiles
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/hooks/useProfiles';
import {
  DEFAULT_NEW_PROFILE,
  DEFAULT_PROFILE_NAME
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentGeneration/Settings/Profile/utils';
import { BEACON_TABS, BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';

export const GENERATION_LIMIT = 20;

const ProductTable = () => {
  const [pageNumber, setPageNumber] = useState(1);

  const {
    allData,
    getCurrentPageData,
    columnDefs,
    searchQuery,
    setSearchQuery,
    selectedCount,
    readyCount,
    readyToReviewCount,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    handleProfileAssignment,
    updateProfilesInCache
  } = useProductTable({ currentPageNumber: pageNumber });

  const { selectedItems, clearSelection, isGeneratingModalOpen, closeGeneratingModal } = useContentGenerationStore();

  const itemsPerPage = ENTITY_TABLE_PAGE_SIZE;
  const currentPageData = getCurrentPageData(pageNumber, itemsPerPage);

  const totalLoadedPages = Math.ceil(allData.length / itemsPerPage);
  const canGoToNextPage = pageNumber < totalLoadedPages || hasNextPage;

  const { openProfileSelectionModal } = useProfileAssignment({
    productData: allData,
    handleProfileAssignment
  });

  const { showSnackbar } = useSnackbar();
  const beaconClientId = useBeaconClientId();
  const { generateContentForSkus, isPending: isGenerating } = useGenerateContentForSkus();
  const { beaconNavigator } = useBeaconNavigator();
  const { setContentGenItemsToReview } = useContentGenReviewStore();
  const { createProfileAsync } = useCreateProfile();
  const { data: activeProfilesData } = useGetProfiles('ACTIVE');

  const getOldestActiveProfile = useCallback(() => {
    if (!activeProfilesData?.data || activeProfilesData.data.length === 0) {
      return null;
    }

    const sortedProfiles = [...activeProfilesData.data].sort((a, b) => {
      if (!a.updatedAt) return 1;
      if (!b.updatedAt) return -1;
      return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    });

    const stacklineGeneratedProfile = sortedProfiles.find(
      (profile) => profile.profileName && profile.profileName.includes(DEFAULT_PROFILE_NAME)
    );

    return stacklineGeneratedProfile || sortedProfiles[0];
  }, [activeProfilesData]);

  const handleReadyToReview = useCallback(() => {
    // Get all selected items for review
    const selectedItemsForReview = allData.filter((item) => selectedItems.has(item.retailerSku));

    if (selectedItemsForReview.length === 0) {
      showSnackbar({
        type: 'error',
        message: 'No items selected for review.'
      });
      return;
    }

    setContentGenItemsToReview(selectedItemsForReview);

    // Navigate to the review page using the proper beacon tab/subtab system
    beaconNavigator.updateSearchParams({ tab: BEACON_TABS.GENERATION, subtab: BEACON_SUBTABS.GENERATION_REVIEW });
    // Use the first item's stacklineSku for the search parameter
    beaconNavigator.updateSearchParams({ sku: selectedItemsForReview[0].stacklineSku });
    // Add contentGenId to indicate this is multi-SKU generation (if available)
    if (selectedItemsForReview[0].skuContentGenerationId) {
      beaconNavigator.updateSearchParams({ contentGenId: selectedItemsForReview[0].skuContentGenerationId });
    }
    beaconNavigator.go();
  }, [allData, selectedItems, setContentGenItemsToReview, beaconNavigator, showSnackbar]);

  const handleGenerate = useCallback(async () => {
    const retailerId = parseInt(getQueryParamValue('rid'));

    if (!retailerId) {
      showSnackbar({
        type: 'error',
        message: 'Missing retailer ID.'
      });
      return;
    }

    if (!beaconClientId) {
      showSnackbar({
        type: 'error',
        message: 'Missing beacon client ID.'
      });
      return;
    }

    // Get selected items - both with and without profiles
    const selectedItemsData = allData.filter((item) => selectedItems.has(item.retailerSku));

    if (selectedItemsData.length === 0) {
      showSnackbar({
        type: 'error',
        message: 'No items selected for generation.'
      });
      return;
    }

    // Check generation limit
    if (selectedItemsData.length > GENERATION_LIMIT) {
      showSnackbar({
        type: 'error',
        message: `Cannot generate content for more than ${GENERATION_LIMIT} items at once. Please select ${GENERATION_LIMIT} or fewer items.`
      });
      return;
    }

    // Separate items with profiles and without profiles
    const itemsWithProfiles = selectedItemsData.filter((item) => item.profileId && item.profileName);

    const itemsWithoutProfiles = selectedItemsData.filter((item) => !item.profileId || !item.profileName);

    let defaultProfileId = '';
    let defaultProfileVersion = 0;
    let defaultProfileName = '';

    // Get existing oldest active profile or create a new one as fallback
    if (itemsWithoutProfiles.length > 0) {
      const oldestActiveProfile = getOldestActiveProfile();
      if (oldestActiveProfile) {
        // Use existing oldest active profile as default
        defaultProfileId = oldestActiveProfile.profileId;
        defaultProfileVersion = oldestActiveProfile.version || 0;
        defaultProfileName = oldestActiveProfile.profileName;
      } else {
        // Fallback: Create a temporary default profile if no active profiles exist
        try {
          const defaultProfile = DEFAULT_NEW_PROFILE;

          const response = await createProfileAsync(defaultProfile);
          if (response.data?.isSuccess && response.data?.data?.profileId) {
            defaultProfileId = response.data.data.profileId;
            defaultProfileVersion = response.data.data.version || 0;
            defaultProfileName = response.data.data.profileName;
          } else {
            throw new Error('Failed to create default profile');
          }
        } catch (error) {
          showSnackbar({
            type: 'error',
            message: 'Failed to create default profile for generation.'
          });
          return;
        }
      }
    }

    // Combine all items for the request
    const allContentRequests = [
      // Items with existing profiles
      ...itemsWithProfiles.map((item) => ({
        retailerSku: item.retailerSku,
        profileId: item.profileId,
        profileVersion: item.profileVersion
      })),
      // Items without profiles - use the newly created default profile
      ...itemsWithoutProfiles.map((item) => ({
        retailerSku: item.retailerSku,
        profileId: defaultProfileId,
        profileVersion: defaultProfileVersion
      }))
    ];

    const requestBody: GenerateContentForSkusRequest = {
      beaconClientId,
      retailerId,
      contentRequests: allContentRequests
    };

    generateContentForSkus(requestBody, {
      onSuccess: (response) => {
        clearSelection();

        // Optimistically update status to GENERATING for selected items
        const selectedItemsForOptimisticUpdate = selectedItemsData.map((item) => ({
          retailerSku: item.retailerSku,
          contentGenId: item.skuContentGenerationId,
          profileId: item.profileId || defaultProfileId,
          profileName: item.profileName || defaultProfileName,
          profileVersion: item.profileVersion || defaultProfileVersion,
          status: GenerationStatusEnum.enum.GENERATING
        }));

        updateProfilesInCache(selectedItemsForOptimisticUpdate);

        // Success snackbar will be shown when generation is complete, not when it starts
      }
    });
  }, [
    beaconClientId,
    allData,
    generateContentForSkus,
    showSnackbar,
    selectedItems,
    clearSelection,
    createProfileAsync,
    updateProfilesInCache,
    getOldestActiveProfile
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  if (isError) {
    return (
      <Flex flexDirection="column" gap={16} alignItems="center" justifyContent="center" height="300px">
        <Text color="error" variant="body1">
          Error loading product data
        </Text>
        <button
          onClick={() => refetch()}
          style={{
            padding: '8px 16px',
            backgroundColor: color.darkBlue,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={32}>
      <Flex justifyContent="space-between" alignItems="center">
        <SearchTextField placeholder="Search products" value={searchQuery} onChange={handleSearchChange} />
        <Flex gap={16}>
          <NestedPill
            text={
              <Flex alignItems="center" gap={8}>
                <BluePencil style={{ width: 20, height: 20 }} />
                <Flex>{selectedCount} selected</Flex>
              </Flex>
            }
            actionText="Assign profiles"
            onActionClick={openProfileSelectionModal}
            innerPillSx={{
              backgroundColor: '#FFF',
              border: `1px solid ${color.graysSecondary}`,
              color: color.darkBlue
            }}
            outerPillSx={{
              backgroundColor: '#FFF'
            }}
            disabled={selectedCount === 0}
          />

          <NestedPill
            text={
              <Flex alignItems="center" gap={8}>
                <BlueSearch style={{ width: 20, height: 20 }} />
                <Flex>{readyToReviewCount} ready</Flex>
              </Flex>
            }
            actionText="Review"
            onActionClick={handleReadyToReview}
            disabled={selectedCount === 0}
            innerPillSx={{
              backgroundColor: '#FFF',
              border: `1px solid ${color.graysSecondary}`,
              color: color.darkBlue
            }}
            outerPillSx={{
              backgroundColor: '#FFF'
            }}
          />

          <NestedPill
            text={
              <Flex alignItems="center" gap={8}>
                <SnackbarCheckmark style={{ width: 25, height: 25 }} />
                <Flex>{readyCount} ready</Flex>
              </Flex>
            }
            actionText={isGenerating ? 'Generating...' : 'Generate'}
            onActionClick={handleGenerate}
            disabled={readyCount === 0 || isGenerating}
            outerPillSx={{
              backgroundColor: '#FFF'
            }}
          />
        </Flex>
      </Flex>
      <EntityTable
        rowData={currentPageData}
        columnDefs={columnDefs}
        shouldModifyColumnDefs={false}
        showPagination={false}
        initialPageSize={itemsPerPage}
        isLoading={isLoading || isFetchingNextPage}
        skeletonRows={20}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 0' }}>
        <Pagination
          currentPage={pageNumber}
          shouldUseInfinitePagination={true}
          onClickNext={() => {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);

            // Check if we need to fetch more data
            const totalLoadedItems = allData.length;
            const requiredItems = nextPage * itemsPerPage;

            if (requiredItems > totalLoadedItems && hasNextPage) {
              fetchNextPage();
            }
          }}
          disableNextButton={!canGoToNextPage || isFetchingNextPage}
          onClickPrevious={() => {
            if (pageNumber > 1) {
              setPageNumber(pageNumber - 1);
            }
          }}
        />
      </div>
      <GeneratingModal open={isGeneratingModalOpen} onClose={closeGeneratingModal} />
      <SelectProfileModal />
      <SingleSelectProfileModal handleProfileAssignment={handleProfileAssignment} />
    </Flex>
  );
};

export default React.memo(ProductTable);
